import { useEffect, useRef } from 'react';
import { Button } from '../../../components/Button';
import type { AdminTourFieldError, AdminTourWriteFailure } from '../../../types/adminTours';

/** A write that failed for a reason the service could not classify. Same banner, same reload. */
interface UnexpectedFailure {
  kind: 'unexpected';
  message: string;
}

interface TourEditorFailureProps {
  failure: AdminTourWriteFailure | UnexpectedFailure;
  /** Validation errors that name no step, so no step row is showing them. */
  unattached: AdminTourFieldError[];
  /** "Title two (calls.export)" for each step a publish refused. */
  blockedLabels: string[];
  onReload: () => void;
}

/**
 * The write failures the editor has a screen for.
 *
 * The reload is offered for anything the admin cannot act on where they are standing: a 409, and a
 * 400 that names no field - which is how the API reports a step that left the manifest between the
 * load and the save, a message about a step the admin never touched. A 400 that does name fields
 * gets no reload, because throwing their edits away to fix a typo would be the wrong trade.
 *
 * Focus moves here when it appears, because the buttons that trigger these writes sit at the
 * bottom of a long page and the banner is at the top - an admin who saves with the keyboard would
 * otherwise be told nothing happened.
 */
export const TourEditorFailure = ({
  failure,
  unattached,
  blockedLabels,
  onReload,
}: TourEditorFailureProps) => {
  const bannerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bannerRef.current?.focus();
  }, [failure]);

  return (
    <div
      ref={bannerRef}
      tabIndex={-1}
      className="admin-error tour-editor__banner"
      role="alert"
      data-testid="tour-editor-failure"
    >
      <p className="tour-editor__banner-message">{failure.message}</p>

      {failure.kind === 'validation' && unattached.length > 0 && (
        <ul className="tour-editor__banner-list">
          {unattached.map((error) => (
            <li key={`${error.field}:${error.message}`}>{error.message}</li>
          ))}
        </ul>
      )}

      {failure.kind === 'blocked' && (
        <ul className="tour-editor__banner-list" data-testid="tour-editor-blocked-steps">
          {blockedLabels.map((label) => (
            <li key={label}>{label}</li>
          ))}
        </ul>
      )}

      {(failure.kind === 'conflict' ||
        failure.kind === 'unexpected' ||
        (failure.kind === 'validation' && failure.errors.length === 0)) && (
        <Button
          type="button"
          variant="secondary"
          onClick={onReload}
          data-testid="tour-editor-reload"
        >
          Reload this tour
        </Button>
      )}
    </div>
  );
};
